import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignDetail = () => {
  const { authAxios } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduling, setScheduling] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [activeTab, setActiveTab] = useState("editor");
  const [expandedPreview, setExpandedPreview] = useState(false);
  const [showTroubleHelp, setShowTroubleHelp] = useState(false);
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  
  // Collapsible sections state - All collapsed by default for cleaner look
  const [expandedSections, setExpandedSections] = useState({
    logo: false,
    headerImage: false,
    layout: false,
    sections: false,
    theme: false,
    sectionImages: false,
    background: false,
    social: false,
    editEmail: false,
    ctaFooter: false,
    aiTools: false,
    images: false,
    accentColor: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // Preview scroll ref for sync
  const previewScrollRef = useRef(null);
  
  // Debounce ref for API calls
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Function to scroll preview to a specific section
  const scrollPreviewTo = (section) => {
    if (previewScrollRef.current) {
      const scrollContainer = previewScrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        const positions = {
          'top': 0,
          'banner': 100,
          'headline': 250,
          'sections': 500,
          'cta': 800,
          'footer': 1000
        };
        scrollContainer.scrollTo({
          top: positions[section] || 0,
          behavior: 'smooth'
        });
      }
    }
  };
  
  // Template editor state
  const [templateSettings, setTemplateSettings] = useState({
    headline: "",
    subheadline: "",
    body_text: "",
    cta_text: "",
    cta_url: "",
    primary_color: "#7c3aed",
    secondary_color: "#6366f1",
    show_articles: true,
    show_social: true,
    footer_text: "",
    theme: "modern",
    layout: "magazine",
    brand_colors: []
  });
  const [saving, setSaving] = useState(false);
  const [themes, setThemes] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [subjectSuggestions, setSubjectSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [generatingContent, setGeneratingContent] = useState(false);
  const [adjustingLength, setAdjustingLength] = useState(false);
  const [updatingSections, setUpdatingSections] = useState(false);
  const [pullingImages, setPullingImages] = useState(false);
  const [pullImagesUrl, setPullImagesUrl] = useState("");
  const [showAllImages, setShowAllImages] = useState(false);
  const [changingUrl, setChangingUrl] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [showChangeUrl, setShowChangeUrl] = useState(false);
  const [sectionImages, setSectionImages] = useState({});
  const [selectingImageFor, setSelectingImageFor] = useState(null);
  const [showBlocks, setShowBlocks] = useState({
    headline: true,
    subheadline: true,
    body: true,
    cta: true
  });
  
  // AI Suggestions state
  const [headlineSuggestions, setHeadlineSuggestions] = useState([]);
  const [summarySuggestions, setSummarySuggestions] = useState([]);
  const [showHeadlineSuggestions, setShowHeadlineSuggestions] = useState(false);
  const [showSummarySuggestions, setShowSummarySuggestions] = useState(false);
  const [loadingHeadlineAI, setLoadingHeadlineAI] = useState(false);
  
  // Send Test Email state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  
  // Social Media Share state
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePlatform, setSharePlatform] = useState("facebook");
  const [sharePost, setSharePost] = useState(null);
  const [generatingShare, setGeneratingShare] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState("");
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Header image upload state
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);

  useEffect(() => {
    loadCampaign();
    loadThemes();
    loadLayouts();
    loadSubjectSuggestions();
  }, [id]);

  const loadCampaign = async () => {
    try {
      const [campaignRes, settingsRes] = await Promise.all([
        authAxios.get(`/campaigns/${id}`),
        authAxios.get(`/campaigns/${id}/template-settings`)
      ]);
      setCampaign(campaignRes.data);
      setNewSubject(campaignRes.data.subject_line || "");
      setTemplateSettings(settingsRes.data);
      // Load existing section images into local state
      if (settingsRes.data.section_images) {
        setSectionImages(settingsRes.data.section_images);
      }
    } catch (err) {
      toast.error("Campaign not found");
    } finally {
      setLoading(false);
    }
  };

  // Debounced save function - waits 500ms after last change before saving
  const debouncedSave = async (newSettings, showToast = false) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setIsSaving(true);
    
    // Wait 500ms before actually saving (allows rapid changes)
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await authAxios.post(`/campaigns/${id}/customize`, newSettings);
        // Only update campaign with the new email_content from response - no extra API call
        if (res.data) {
          setCampaign(prev => prev ? {
            ...prev,
            email_content: res.data.email_content || prev.email_content,
            subject_line: res.data.subject || prev.subject_line,
            template_settings: res.data.template_settings || newSettings
          } : prev);
        }
        if (showToast) {
          toast.success("Changes saved!");
        }
      } catch (err) {
        toast.error("Failed to save changes");
      } finally {
        setIsSaving(false);
      }
    }, 500);
  };

  // Quick update function - updates UI immediately, saves in background
  const quickUpdate = (newSettings, toastMsg = null) => {
    setTemplateSettings(newSettings);
    debouncedSave(newSettings, !!toastMsg);
  };
  
  // Debounced update for text inputs - longer delay for typing
  const debouncedTextUpdate = (newSettings) => {
    setTemplateSettings(newSettings);
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setIsSaving(true);
    // Longer delay for text input (800ms) to batch keystrokes
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await authAxios.post(`/campaigns/${id}/customize`, newSettings);
        if (res.data) {
          setCampaign(prev => prev ? {
            ...prev,
            email_content: res.data.email_content || prev.email_content,
            subject_line: res.data.subject || prev.subject_line,
            template_settings: res.data.template_settings || newSettings
          } : prev);
        }
      } catch (err) {
        toast.error("Failed to save changes");
      } finally {
        setIsSaving(false);
      }
    }, 800);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo file must be less than 2MB");
      return;
    }
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await authAxios.post(`/campaigns/${id}/upload-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo uploaded successfully!");
      // Update template settings with new logo
      setTemplateSettings({...templateSettings, uploaded_logo: res.data.logo_url});
      // Refresh preview
      await authAxios.post(`/campaigns/${id}/customize`, {...templateSettings, uploaded_logo: res.data.logo_url});
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handler for uploading header/banner image from desktop
  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB for header images)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file must be less than 5MB");
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file (PNG, JPG, GIF, WebP)");
      return;
    }
    
    setUploadingHeaderImage(true);
    
    try {
      // Convert to base64 data URL for simplicity
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Url = reader.result;
        
        // Add uploaded image to the library (additional_images) instead of replacing main image
        const existingImages = templateSettings.additional_images || [];
        const newSettings = {
          ...templateSettings, 
          additional_images: [base64Url, ...existingImages], // Add to beginning of library
          uploaded_header_image: base64Url
        };
        setTemplateSettings(newSettings);
        
        try {
          await authAxios.post(`/campaigns/${id}/customize`, newSettings);
          toast.success("Image added to library! Click on it to use as header.");
          loadCampaign();
        } catch (err) {
          toast.error("Failed to save image");
        }
        setUploadingHeaderImage(false);
      };
      reader.onerror = () => {
        toast.error("Failed to read image file");
        setUploadingHeaderImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Failed to upload image");
      setUploadingHeaderImage(false);
    }
  };

  const handleGenerateContent = async () => {
    setGeneratingContent(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/generate-content`);
      toast.success("AI content generated!");
      // Reload campaign to get updated content
      loadCampaign();
    } catch (err) {
      toast.error("Failed to generate content. Please try again.");
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleAdjustLength = async (action, intensity) => {
    setAdjustingLength(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/adjust-length`, { action, intensity });
      toast.success(`Email made ${action}!`);
      // Update template settings with new content
      setTemplateSettings({
        ...templateSettings,
        headline: res.data.headline,
        subheadline: res.data.subheadline,
        body_text: res.data.body_text,
        ai_generated: true
      });
      // Reload to get updated preview
      loadCampaign();
    } catch (err) {
      toast.error("Failed to adjust length. Please try again.");
    } finally {
      setAdjustingLength(false);
    }
  };

  const toggleBlock = (block) => {
    const newShowBlocks = { ...showBlocks, [block]: !showBlocks[block] };
    setShowBlocks(newShowBlocks);
    
    // Update template settings to reflect hidden blocks
    const updates = {};
    if (!newShowBlocks.headline) updates.headline = "";
    if (!newShowBlocks.subheadline) updates.subheadline = "";
    if (!newShowBlocks.body) updates.body_text = "";
    if (!newShowBlocks.cta) updates.cta_text = "";
    
    // Don't clear content, just track visibility
    setTemplateSettings({
      ...templateSettings,
      hidden_blocks: Object.keys(newShowBlocks).filter(k => !newShowBlocks[k])
    });
  };

  const handleUpdateSections = async (numSections) => {
    setUpdatingSections(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/update-sections`, { num_sections: numSections });
      toast.success(`Updated to ${numSections} sections!`);
      if (res.data.body_text) {
        setTemplateSettings({
          ...templateSettings,
          num_sections: numSections,
          body_text: res.data.body_text,
          ai_generated: true
        });
      } else {
        setTemplateSettings({...templateSettings, num_sections: numSections});
      }
      loadCampaign();
    } catch (err) {
      toast.error("Failed to update sections");
    } finally {
      setUpdatingSections(false);
    }
  };

  const handlePullImages = async () => {
    if (!pullImagesUrl) return;
    setPullingImages(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/pull-images`, { url: pullImagesUrl });
      toast.success(res.data.message);
      setPullImagesUrl("");
      loadCampaign(); // Reload to get updated images
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pull images");
    } finally {
      setPullingImages(false);
    }
  };

  const handleChangeUrl = async () => {
    if (!newUrl) return;
    setChangingUrl(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/change-url`, { 
        url: newUrl, 
        keep_customizations: true 
      });
      toast.success(`URL changed! Found ${res.data.images_found} images.`);
      setNewUrl("");
      setShowChangeUrl(false);
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change URL");
    } finally {
      setChangingUrl(false);
    }
  };

  const handleSelectSectionImage = (sectionIdx, imageUrl) => {
    const newSectionImages = { ...sectionImages, [sectionIdx]: imageUrl };
    setSectionImages(newSectionImages);
    setSelectingImageFor(null);
    
    // Save to backend
    authAxios.post(`/campaigns/${id}/section-images`, { section_images: newSectionImages })
      .then(() => {
        toast.success("Image added to section!");
        loadCampaign();
      })
      .catch(() => toast.error("Failed to update section image"));
  };

  const handleRemoveSectionImage = (sectionIdx) => {
    const newSectionImages = { ...sectionImages };
    delete newSectionImages[sectionIdx];
    setSectionImages(newSectionImages);
    
    authAxios.post(`/campaigns/${id}/section-images`, { section_images: newSectionImages })
      .then(() => {
        toast.success("Image removed from section!");
        loadCampaign();
      })
      .catch(() => toast.error("Failed to remove image"));
  };

  const loadThemes = async () => {
    try {
      const res = await authAxios.get("/templates/themes");
      setThemes(res.data.themes);
    } catch (err) {
      console.error("Failed to load themes");
    }
  };

  const loadLayouts = async () => {
    try {
      const res = await authAxios.get("/templates/layouts");
      setLayouts(res.data.layouts);
    } catch (err) {
      console.error("Failed to load layouts");
    }
  };

  const loadSubjectSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await authAxios.get(`/campaigns/${id}/subject-suggestions`);
      setSubjectSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error("Failed to load subject suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const updateSubjectLine = async (subject) => {
    try {
      await authAxios.put(`/campaigns/${id}`, { subject_line: subject });
      setCampaign({...campaign, subject_line: subject});
      setNewSubject(subject);
      setEditingSubject(false);
      toast.success("Subject line updated!");
    } catch (err) {
      toast.error("Failed to update subject");
    }
  };

  // Handle recipient file upload from campaign detail
  const handleRecipientUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      toast.loading("Uploading recipients...");
      const res = await authAxios.post(`/campaigns/${id}/upload-emails`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.dismiss();
      toast.success(`${res.data.count || 'Recipients'} contacts uploaded!`);
      loadCampaign(); // Refresh to show new recipients
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "Failed to upload recipients");
    }
    // Reset input
    e.target.value = '';
  };

  // Use all contacts from master list
  const handleUseAllContacts = async () => {
    try {
      toast.loading("Adding contacts...");
      const res = await authAxios.post(`/contacts/use-all-for-campaign/${id}`);
      toast.dismiss();
      toast.success(res.data.message);
      loadCampaign();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.detail || "No contacts found. Import contacts first.");
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await authAxios.post(`/campaigns/${id}/send`);
      toast.success("Boom! Emails are being sent!");
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmailAddress) {
      toast.error("Please enter an email address");
      return;
    }
    setSendingTest(true);
    try {
      const res = await authAxios.post(`/campaigns/${id}/send-test`, { email: testEmailAddress });
      toast.success(res.data.message || "Test email sent!");
      setShowTestEmailModal(false);
      setTestEmailAddress("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const handleGenerateSocialPost = async (platform) => {
    setGeneratingShare(true);
    setSharePlatform(platform);
    try {
      const res = await authAxios.post(`/campaigns/${id}/generate-social-post`, {
        platform: platform,
        image_url: shareImageUrl || templateSettings.main_image
      });
      setSharePost(res.data);
    } catch (err) {
      toast.error("Failed to generate social post");
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleCopyPostText = () => {
    if (sharePost?.post_text) {
      const fullText = sharePost.post_text + "\n\n" + sharePost.hashtags.join(" ");
      navigator.clipboard.writeText(fullText);
      toast.success("Post copied to clipboard!");
    }
  };

  const handleDownloadImage = async () => {
    const imageUrl = sharePost?.image_url || templateSettings.main_image;
    if (imageUrl) {
      try {
        window.open(imageUrl, '_blank');
        toast.success("Image opened in new tab - right-click to save it!");
      } catch (err) {
        toast.error("Failed to open image");
      }
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) return;
    setScheduling(true);
    try {
      const dateStr = format(scheduleDate, "yyyy-MM-dd");
      const res = await authAxios.post(`/campaigns/${id}/schedule-with-time`, {
        scheduled_date: dateStr,
        scheduled_time: scheduleTime,
        timezone: "UTC"
      });
      toast.success(res.data.message || `Campaign scheduled!`);
      setShowScheduleModal(false);
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule");
    } finally {
      setScheduling(false);
    }
  };

  const handleUnschedule = async () => {
    try {
      await authAxios.post(`/campaigns/${id}/unschedule`);
      toast.success("Campaign unscheduled");
      loadCampaign();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unschedule");
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      await authAxios.post(`/campaigns/${id}/regenerate`);
      toast.success("Email regenerated!");
      loadCampaign();
    } catch (err) {
      toast.error("Failed to regenerate");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await authAxios.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      navigate("/campaigns");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await authAxios.post(`/campaigns/${id}/customize`, templateSettings);
      toast.success("Template saved!");
      loadCampaign();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const colorPresets = [
    { name: "Violet", color: "#7c3aed" },
    { name: "Blue", color: "#2563eb" },
    { name: "Emerald", color: "#059669" },
    { name: "Rose", color: "#e11d48" },
    { name: "Amber", color: "#d97706" },
    { name: "Indigo", color: "#4f46e5" },
    { name: "Teal", color: "#0d9488" },
    { name: "Pink", color: "#db2777" },
  ];

  if (loading) return <Layout><LoadingScreen /></Layout>;
  if (!campaign) return <Layout><div className="p-10 text-center">Campaign not found</div></Layout>;

  // Check if this is a new/fresh campaign (no customization done yet)
  const isNewCampaign = !campaign.email_content || campaign.email_content.length < 500;

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="campaign-detail">
        {/* Beginner Guide - Shows for new campaigns */}
        {isNewCampaign && activeTab === "preview" && (
          <div className="mb-6 bg-gradient-to-r from-afa-navy to-afa-teal rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-afa-lime" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-1" style={{fontFamily: 'Manrope'}}>Your email is ready!</h2>
                <p className="text-white/80 text-sm">Use the three steps above to design, preview, and send.</p>
              </div>
            </div>
          </div>
        )}

        {/* Simplified Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/campaigns')} className="text-slate-400 hover:text-slate-600">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Manrope'}}>
                  {campaign.name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`status-${campaign.status}`}>{campaign.status}</Badge>
                  {campaign.email_list?.length > 0 && (
                    <span className="text-sm text-slate-500">{campaign.email_list.length} recipients</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Simple action buttons */}
            <div className="flex items-center gap-2">
              {campaign.status === "draft" && (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowTestEmailModal(true)}
                    className="rounded-full"
                  >
                    <Mail className="w-4 h-4 mr-1.5" /> Test
                  </Button>
                  <Button 
                    onClick={() => setActiveTab("send")}
                    className="bg-afa-lime text-afa-navy hover:bg-afa-lime/90 rounded-full font-semibold px-5"
                  >
                    <Send className="w-4 h-4 mr-1.5" />
                    Send
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Send Test Email Modal */}
        {showTestEmailModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowTestEmailModal(false)} />
            <Card className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <Mail className="w-5 h-5 text-afa-navy" />
                  Send Test Email
                </CardTitle>
                <CardDescription>
                  Send a test email to yourself before sending to your list
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Email Address</Label>
                  <Input
                    data-testid="test-email-input"
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="your@email.com"
                    className="h-12 rounded-xl"
                    onKeyDown={(e) => e.key === 'Enter' && handleSendTest()}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowTestEmailModal(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="confirm-send-test-btn"
                    onClick={handleSendTest}
                    disabled={sendingTest || !testEmailAddress}
                    className="flex-1 bg-afa-navy hover:bg-afa-navy-dark rounded-full"
                  >
                    {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    Send Test
                  </Button>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Subject line will be prefixed with [TEST]
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowScheduleModal(false)} />
            <Card className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <CalendarIcon className="w-5 h-5 text-afa-navy" />
                  Schedule Campaign
                </CardTitle>
                <CardDescription>
                  Pick a date and time to automatically send your email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Calendar */}
                <div className="border rounded-xl p-3">
                  <Calendar
                    mode="single"
                    selected={scheduleDate}
                    onSelect={setScheduleDate}
                    disabled={(date) => date < new Date()}
                    className="mx-auto"
                  />
                </div>

                {/* Time Picker */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Time (UTC)</Label>
                  <div className="flex items-center gap-2">
                    <select
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="flex-1 h-12 px-4 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-afa-navy"
                    >
                      {[...Array(24)].map((_, h) => (
                        <React.Fragment key={h}>
                          <option value={`${h.toString().padStart(2, '0')}:00`}>{h.toString().padStart(2, '0')}:00</option>
                          <option value={`${h.toString().padStart(2, '0')}:30`}>{h.toString().padStart(2, '0')}:30</option>
                        </React.Fragment>
                      ))}
                    </select>
                    <Clock className="w-5 h-5 text-slate-400" />
                  </div>
                </div>

                {/* Summary */}
                {scheduleDate && (
                  <div className="p-4 bg-afa-navy-light rounded-xl">
                    <p className="text-sm text-slate-600">Campaign will be sent on:</p>
                    <p className="text-lg font-semibold text-afa-navy mt-1">
                      {format(scheduleDate, "EEEE, MMMM d, yyyy")} at {scheduleTime} UTC
                    </p>
                  </div>
                )}

                {/* Recipients Warning */}
                {(!campaign.email_list || campaign.email_list.length === 0) && (
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Add recipients before scheduling. Your campaign has no contacts yet.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSchedule}
                    disabled={scheduling || !scheduleDate || !campaign.email_list?.length}
                    className="flex-1 bg-afa-navy hover:bg-afa-navy-dark rounded-full"
                  >
                    {scheduling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarIcon className="w-4 h-4 mr-1" />}
                    Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Social Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowShareModal(false)} />
            <Card className="relative z-10 w-full max-w-xl mx-4 bg-white rounded-2xl shadow-xl animate-fade-in max-h-[90vh] overflow-hidden">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <Share2 className="w-5 h-5 text-emerald-600" />
                  Create Social Media Post
                </CardTitle>
                <CardDescription>
                  Generate a post from your email content for Facebook or Instagram
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {/* Platform Tabs */}
                <div className="flex border-b">
                  <button
                    onClick={() => handleGenerateSocialPost("facebook")}
                    className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors ${
                      sharePlatform === "facebook" 
                        ? "bg-blue-50 text-blue-600 border-b-2 border-blue-600" 
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                    data-testid="facebook-tab"
                  >
                    <Facebook className="w-4 h-4" />
                    Facebook
                  </button>
                  <button
                    onClick={() => handleGenerateSocialPost("instagram")}
                    className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors ${
                      sharePlatform === "instagram" 
                        ? "bg-pink-50 text-pink-600 border-b-2 border-pink-600" 
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                    data-testid="instagram-tab"
                  >
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </button>
                </div>

                <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                  {/* Generated Post */}
                  {generatingShare ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
                      <p className="text-slate-500">Generating {sharePlatform} post...</p>
                    </div>
                  ) : sharePost ? (
                    <>
                      {/* Post Text */}
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 flex items-center justify-between">
                          <span>Post Text</span>
                          <span className="text-xs font-normal text-slate-400">
                            {sharePost.character_count} / {sharePost.max_characters} chars
                          </span>
                        </Label>
                        <textarea
                          data-testid="share-post-text"
                          value={sharePost.post_text + "\n\n" + sharePost.hashtags.join(" ")}
                          onChange={(e) => {
                            const text = e.target.value;
                            const hashtagMatch = text.match(/#\w+/g) || [];
                            const postPart = text.replace(/#\w+\s*/g, '').trim();
                            setSharePost({
                              ...sharePost,
                              post_text: postPart,
                              hashtags: hashtagMatch,
                              character_count: postPart.length
                            });
                          }}
                          className="w-full h-48 p-3 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          placeholder="Your social media post..."
                        />
                      </div>

                      {/* Step-by-step instructions - BEFORE action buttons */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                          <span>📋</span> Before you click - here's what will happen:
                        </p>
                        <ol className="text-sm text-amber-900 space-y-2 list-decimal list-inside">
                          {sharePlatform === "facebook" ? (
                            <>
                              <li>Your text will be <strong>copied automatically</strong></li>
                              <li>Facebook opens in a new tab</li>
                              <li>Click <strong>"What's on your mind?"</strong> to start a post</li>
                              <li><strong>Paste</strong> your text (Ctrl+V or Cmd+V)</li>
                              <li>Click the <strong>photo icon 📷</strong> to add an image from your computer</li>
                              <li>Hit <strong>Post</strong>!</li>
                            </>
                          ) : (
                            <>
                              <li><strong>First</strong>, click <strong>"Download Image"</strong> below to save a photo</li>
                              <li>Then click <strong>"Post to Instagram"</strong> - text copies automatically</li>
                              <li>On Instagram, tap <strong>+</strong> to create a post</li>
                              <li>Select the image you just saved</li>
                              <li><strong>Paste</strong> your text into the caption</li>
                              <li>Share it!</li>
                            </>
                          )}
                        </ol>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3 pt-2">
                        {/* Primary action - Post directly */}
                        <Button
                          onClick={() => {
                            // Copy text to clipboard
                            const fullText = sharePost.post_text + "\n\n" + sharePost.hashtags.join(" ");
                            navigator.clipboard.writeText(fullText);
                            
                            // Open appropriate platform
                            if (sharePlatform === "facebook") {
                              window.open("https://www.facebook.com/", "_blank");
                              toast.success("Text copied! Create a new post on Facebook and paste your content.");
                            } else {
                              window.open("https://www.instagram.com/", "_blank");
                              toast.success("Text copied! Open Instagram app on your phone to create a post.");
                            }
                          }}
                          className={`w-full rounded-full text-white ${
                            sharePlatform === "facebook" 
                              ? "bg-blue-600 hover:bg-blue-700" 
                              : "bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500"
                          }`}
                          data-testid="post-to-platform-btn"
                        >
                          {sharePlatform === "facebook" ? (
                            <Facebook className="w-4 h-4 mr-2" />
                          ) : (
                            <Instagram className="w-4 h-4 mr-2" />
                          )}
                          Post to {sharePlatform === "facebook" ? "Facebook" : "Instagram"}
                        </Button>
                        
                        {/* Secondary actions */}
                        <div className="flex gap-3">
                          <Button
                            onClick={handleCopyPostText}
                            variant="outline"
                            className="flex-1 rounded-full"
                            data-testid="copy-post-btn"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Text
                          </Button>
                          <Button
                            onClick={handleDownloadImage}
                            variant="outline"
                            className="flex-1 rounded-full"
                            disabled={!sharePost.image_url && !templateSettings.main_image}
                            data-testid="download-image-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download Image
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      Select a platform to generate a post
                    </div>
                  )}
                </div>

                {/* Close button */}
                <div className="p-4 border-t bg-slate-50">
                  <Button
                    variant="outline"
                    onClick={() => setShowShareModal(false)}
                    className="w-full rounded-full"
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Workflow stepper — Customize → Preview → Send */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-white border border-slate-200 p-1.5 rounded-full shadow-sm gap-1">
            <TabsTrigger
              value="editor"
              className="rounded-full px-5 py-2.5 text-sm font-medium transition-all flex items-center gap-2"
              data-testid="editor-tab"
              style={{
                background: activeTab === 'editor' ? 'linear-gradient(135deg, #B7EB1B 0%, #E8F9A0 100%)' : 'transparent',
                color: activeTab === 'editor' ? '#003858' : '#475569',
                boxShadow: activeTab === 'editor' ? '0 4px 12px rgba(183, 235, 27, 0.4)' : 'none'
              }}
            >
              <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                activeTab === 'editor' ? 'bg-[#003858] text-[#B7EB1B]' : 'bg-slate-200 text-slate-600'
              }`}>1</span>
              <Palette className="w-4 h-4" />
              Design
            </TabsTrigger>

            <ChevronRight className="w-4 h-4 text-slate-300 mx-0.5" aria-hidden="true" />

            <TabsTrigger
              value="preview"
              className="rounded-full px-5 py-2.5 text-sm font-medium transition-all flex items-center gap-2"
              style={{
                background: activeTab === 'preview' ? 'linear-gradient(135deg, #119AB3 0%, #67D4E8 100%)' : 'transparent',
                color: activeTab === 'preview' ? 'white' : '#475569',
                boxShadow: activeTab === 'preview' ? '0 4px 12px rgba(17, 154, 179, 0.3)' : 'none'
              }}
            >
              <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                activeTab === 'preview' ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'
              }`}>2</span>
              <Eye className="w-4 h-4" />
              Preview
            </TabsTrigger>

            <ChevronRight className="w-4 h-4 text-slate-300 mx-0.5" aria-hidden="true" />

            <TabsTrigger
              value="send"
              className="rounded-full px-5 py-2.5 text-sm font-medium transition-all flex items-center gap-2"
              data-testid="send-tab"
              style={{
                background: activeTab === 'send' ? 'linear-gradient(135deg, #003858 0%, #119AB3 100%)' : 'transparent',
                color: activeTab === 'send' ? 'white' : '#475569',
                boxShadow: activeTab === 'send' ? '0 4px 12px rgba(0, 56, 88, 0.3)' : 'none'
              }}
            >
              <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${
                activeTab === 'send' ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'
              }`}>3</span>
              <Send className="w-4 h-4" />
              Send
            </TabsTrigger>

            {campaign.status !== "draft" && (
              <>
                <span className="w-px h-6 bg-slate-200 mx-1" aria-hidden="true" />
                <TabsTrigger
                  value="analytics"
                  className="rounded-full px-5 py-2.5 text-sm font-medium transition-all flex items-center gap-2"
                  style={{
                    background: activeTab === 'analytics' ? 'linear-gradient(135deg, #10B981 0%, #34D399 100%)' : 'transparent',
                    color: activeTab === 'analytics' ? 'white' : '#475569',
                    boxShadow: activeTab === 'analytics' ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                  }}
                >
                  <BarChart3 className="w-4 h-4" />
                  Analytics
                </TabsTrigger>
              </>
            )}
          </TabsList>
          
          {/* Tab descriptions for beginners */}
          <div className="mt-3 text-sm text-slate-500">
            {activeTab === "editor" && "Step 1: Make it yours — change colors, themes, text, and images"}
            {activeTab === "preview" && "Step 2: See how your email will look to recipients"}
            {activeTab === "send" && "Step 3: Add your contact list and send your email"}
            {activeTab === "analytics" && "Track opens, clicks, and engagement"}
          </div>
        </Tabs>

        {/* Preview Tab */}
        {activeTab === "preview" && (
          <div className="space-y-6">
            {/* Full-width Email Client Mockup */}
            <Card className="bg-white border-slate-200 rounded-2xl overflow-visible shadow-lg relative">
              {/* Email Client Header - looks like Gmail/Outlook */}
              <div className="bg-slate-800 px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="flex-1 mx-4">
                  <div className="bg-slate-700 rounded-md px-3 py-1 text-slate-400 text-xs max-w-md">
                    mail.google.com/mail/inbox
                  </div>
                </div>
                <Tabs value={previewMode} onValueChange={setPreviewMode}>
                  <TabsList className="h-7 bg-slate-700">
                    <TabsTrigger value="desktop" className="text-xs px-3 py-1 text-slate-300 data-[state=active]:bg-slate-600 data-[state=active]:text-white">Desktop</TabsTrigger>
                    <TabsTrigger value="mobile" className="text-xs px-3 py-1 text-slate-300 data-[state=active]:bg-slate-600 data-[state=active]:text-white">Mobile</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              {/* Email Client Toolbar */}
              <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-4">
                <div className="flex items-center gap-2 text-slate-500">
                  <button className="p-1.5 hover:bg-slate-200 rounded"><ChevronLeft className="w-4 h-4" /></button>
                  <button className="p-1.5 hover:bg-slate-200 rounded"><Trash2 className="w-4 h-4" /></button>
                  <button className="p-1.5 hover:bg-slate-200 rounded"><Mail className="w-4 h-4" /></button>
                </div>
                <div className="flex-1"></div>
                <span className="text-xs text-slate-400">1 of 24</span>
              </div>
              
              {/* Email Header Info */}
              <div className="bg-white border-b border-slate-100 px-6 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">{campaign.subject_line || "Your Email Subject"}</h2>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-afa-navy-light flex items-center justify-center text-afa-navy font-semibold">
                        {campaign.scraped_data?.site_name?.[0]?.toUpperCase() || "E"}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{campaign.scraped_data?.site_name || "Your Company"}</span>
                          <span className="text-slate-400 text-sm">&lt;newsletter@{campaign.scraped_data?.url?.replace(/https?:\/\//, '').split('/')[0] || 'company.com'}&gt;</span>
                        </div>
                        <div className="text-sm text-slate-500">to me</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-slate-400">
                      {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    {/* Having Trouble? Button - Always visible in header */}
                    <button 
                      onClick={() => setShowTroubleHelp(true)}
                      className="bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-100 transition-all flex items-center gap-1.5"
                      data-testid="having-trouble-btn"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      Having Trouble?
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Email Content */}
              <div className={`bg-slate-100 ${previewMode === "mobile" ? "flex justify-center py-4" : ""} rounded-b-2xl`}>
                {previewMode === "mobile" ? (
                  /* Mobile Preview uses iframe so CSS media queries work properly */
                  <div className="bg-slate-800 rounded-[2rem] p-2 shadow-2xl mx-4">
                    {/* Phone notch */}
                    <div className="bg-slate-800 h-6 flex justify-center items-center relative">
                      <div className="bg-black w-20 h-5 rounded-b-xl absolute top-0"></div>
                    </div>
                    <iframe
                      srcDoc={campaign.email_content}
                      title="Mobile Email Preview"
                      className="w-[375px] h-[667px] rounded-b-[1.5rem] border-0"
                      style={{ display: 'block', background: 'transparent' }}
                    />
                  </div>
                ) : (
                  /* Desktop Preview renders directly */
                  <ScrollArea className="h-[700px]" ref={previewScrollRef}>
                    <div 
                      className="email-preview-content"
                      dangerouslySetInnerHTML={{ __html: campaign.email_content }} 
                    />
                  </ScrollArea>
                )}
              </div>
            </Card>
            
            {/* Troubleshooting Help Modal */}
            <TroubleshootingHelp isOpen={showTroubleHelp} onClose={() => setShowTroubleHelp(false)} />

            {/* Campaign Details - Below Preview */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white border-slate-100 rounded-2xl">
                <CardHeader>
                  <CardTitle style={{fontFamily: 'Manrope'}}>Campaign Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-slate-500">Subject Line</p>
                      <button 
                        onClick={() => setEditingSubject(!editingSubject)}
                        className="text-xs text-afa-navy hover:underline"
                      >
                        {editingSubject ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                    
                    {editingSubject ? (
                      <div className="space-y-3">
                        <Input
                          value={newSubject}
                          onChange={(e) => setNewSubject(e.target.value)}
                          className="h-10 rounded-lg"
                          placeholder="Enter subject line..."
                        />
                        <Button 
                          onClick={() => updateSubjectLine(newSubject)}
                          size="sm"
                          className="bg-afa-navy hover:bg-afa-navy-dark"
                        >
                          Save
                        </Button>
                        
                        {/* Subject Suggestions */}
                        {(subjectSuggestions.length > 0 || loadingSuggestions) && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-3 h-3 text-afa-navy" />
                              <span className="text-xs font-semibold text-slate-600">Suggestions</span>
                              {loadingSuggestions && <Loader2 className="w-3 h-3 animate-spin text-afa-navy" />}
                            </div>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {subjectSuggestions.map((suggestion, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => {
                                    setNewSubject(suggestion.text);
                                    updateSubjectLine(suggestion.text);
                                  }}
                                  className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-afa-navy hover:bg-afa-navy-light transition-all"
                                  data-testid={`detail-suggestion-${idx}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="text-xs text-slate-800 line-clamp-2">{suggestion.text}</span>
                                    <Badge 
                                      variant="outline" 
                                      className={`text-[10px] flex-shrink-0 ${
                                        suggestion.type === 'ai' 
                                          ? 'border-afa-navy text-afa-navy bg-afa-navy-light' 
                                          : 'border-slate-300 text-slate-500'
                                      }`}
                                    >
                                      {suggestion.type === 'ai' ? 'AI' : 'Page'}
                                    </Badge>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="font-semibold text-slate-900">{campaign.subject_line}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-slate-500">Source URL</p>
                      <button 
                        onClick={() => setShowChangeUrl(!showChangeUrl)}
                        className="text-xs text-afa-navy hover:underline"
                      >
                        {showChangeUrl ? 'Cancel' : 'Change URL'}
                      </button>
                    </div>
                    
                    {showChangeUrl ? (
                      <div className="space-y-2">
                        <Input
                          value={newUrl}
                          onChange={(e) => setNewUrl(e.target.value)}
                          placeholder="https://example.com/new-page"
                          className="h-10 rounded-lg text-sm"
                        />
                        <Button 
                          onClick={handleChangeUrl}
                          disabled={changingUrl || !newUrl}
                          size="sm"
                          className="bg-afa-navy hover:bg-afa-navy-dark"
                        >
                          {changingUrl ? <><Loader2 className="w-3 h-3 animate-spin mr-2" /> Updating...</> : 'Update & Re-scrape'}
                        </Button>
                        <p className="text-xs text-slate-400">This will pull new content and images from the URL</p>
                      </div>
                    ) : (
                      <a href={campaign.website_url} target="_blank" rel="noopener noreferrer" className="text-afa-navy hover:underline text-sm truncate block">
                        {campaign.website_url}
                      </a>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Created</p>
                    <p className="text-slate-700">{new Date(campaign.created_at).toLocaleDateString()}</p>
                  </div>
                  {campaign.scheduled_at && campaign.status === "scheduled" && (
                    <div className="col-span-2 p-4 bg-amber-50 rounded-xl border border-amber-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-amber-600" />
                            <p className="text-sm font-semibold text-amber-800">Scheduled</p>
                          </div>
                          <p className="text-amber-700 font-medium">
                            {new Date(campaign.scheduled_at).toLocaleString(undefined, {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })} UTC
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUnschedule}
                          className="rounded-full border-amber-200 text-amber-700 hover:bg-amber-100"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* "Next" step CTA — only on Preview tab for drafts */}
        {activeTab === "preview" && campaign.status === "draft" && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#003858]/5 to-[#119AB3]/5 border border-[#003858]/20" data-testid="next-step-cta-preview">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#003858]/10 flex items-center justify-center">
                <Send className="w-5 h-5 text-[#003858]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Ready to send?</p>
                <p className="text-sm text-slate-600">Step 3 of 3 — add your contact list and hit send.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setActiveTab("editor")}
                data-testid="back-to-customize-btn"
                className="rounded-full font-medium h-11 border-slate-300"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Design
              </Button>
              <Button
                onClick={() => setActiveTab("send")}
                data-testid="next-step-send-btn"
                className="bg-[#003858] hover:bg-[#003858]/90 text-white rounded-full font-semibold px-6 h-11"
              >
                Set up sending <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
        {activeTab === "editor" && (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            {/* Editor Form */}
            <div className="space-y-3">
              
              {/* 1. Logo */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.logo ? 'brand-card-expanded' : 'brand-card'}`} onMouseEnter={() => scrollPreviewTo('top')}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('logo')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        <span className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-afa-navy" />
                          Your Logo
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Upload your logo to appear at the top of your email
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {(templateSettings.uploaded_logo || campaign?.scraped_data?.logo) && (
                        <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Set</Badge>
                      )}
                      {expandedSections.logo ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.logo && (
                <CardContent>
                  {/* Current Logo Display */}
                  {(templateSettings.uploaded_logo || campaign?.scraped_data?.logo) && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-xl flex items-center gap-4">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <img 
                          src={templateSettings.uploaded_logo || campaign?.scraped_data?.logo} 
                          alt="Current logo" 
                          className="max-h-16 max-w-[180px] object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">
                          {templateSettings.uploaded_logo ? "Your uploaded logo" : "Auto-detected logo"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {templateSettings.uploaded_logo ? "Click below to change" : `From ${campaign?.scraped_data?.site_name || 'your website'}`}
                        </p>
                      </div>
                      <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>
                        {templateSettings.uploaded_logo ? "Uploaded" : "Auto"}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <label 
                    htmlFor="logo-upload"
                    className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                      uploadingLogo ? 'border-afa-navy bg-afa-navy-light' : 'border-slate-200 hover:border-afa-navy hover:bg-afa-navy-light'
                    }`}
                  >
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-5 h-5 text-afa-navy animate-spin" />
                        <span className="text-sm font-medium text-afa-navy">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-sm font-medium text-slate-600">
                          {templateSettings.uploaded_logo ? "Upload New Logo" : "Upload Logo"}
                        </span>
                      </>
                    )}
                  </label>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    PNG, JPG, GIF, or SVG — max 2MB
                  </p>
                  
                  {/* Clear uploaded logo button */}
                  {templateSettings.uploaded_logo && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={async () => {
                        setTemplateSettings({...templateSettings, uploaded_logo: ''});
                        await authAxios.post(`/campaigns/${id}/customize`, {...templateSettings, uploaded_logo: ''});
                        loadCampaign();
                        toast.success("Logo removed");
                      }}
                      className="w-full mt-2 text-slate-500 hover:text-red-600"
                    >
                      Remove uploaded logo
                    </Button>
                  )}
                </CardContent>
                )}
              </Card>

              {/* 2. Header Image */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.headerImage ? 'brand-card-expanded' : 'brand-card'}`} onMouseEnter={() => scrollPreviewTo('banner')}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('headerImage')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        <span className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-afa-navy" />
                          Header Image
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Choose or upload the main banner image
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {templateSettings.main_image && (
                        <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Set</Badge>
                      )}
                      {expandedSections.headerImage ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.headerImage && (
                <CardContent>
                  {/* Upload from desktop option */}
                  <div className="mb-4 pb-4 border-b border-slate-100">
                    <label 
                      className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                        uploadingHeaderImage ? 'border-afa-navy bg-afa-navy-light' : 'border-slate-300 hover:border-afa-navy hover:bg-afa-navy-light'
                      }`}
                    >
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={handleHeaderImageUpload}
                        disabled={uploadingHeaderImage}
                        data-testid="header-image-upload"
                      />
                      {uploadingHeaderImage ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-5 h-5 text-afa-navy animate-spin" />
                          <span className="text-sm text-afa-navy">Uploading...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-slate-400 mb-1" />
                          <span className="text-sm font-medium text-slate-600">Upload from your computer</span>
                          <span className="text-xs text-slate-400">PNG, JPG, GIF, WebP — max 5MB</span>
                        </>
                      )}
                    </label>
                  </div>
                  
                  <p className="text-xs text-slate-500 mb-3">Or choose from scraped images:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {/* Main image */}
                    {templateSettings.main_image && (
                      <button
                        onClick={() => {
                          const newSettings = {...templateSettings, selected_image: templateSettings.main_image};
                          quickUpdate(newSettings);
                        }}
                        className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                          (templateSettings.selected_image || templateSettings.main_image) === templateSettings.main_image
                            ? 'border-afa-navy ring-2 ring-afa-navy'
                            : 'border-slate-200 hover:border-afa-navy'
                        }`}
                      >
                        <img 
                          src={templateSettings.main_image} 
                          alt="Main" 
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <div className="absolute top-2 left-2">
                          <Badge className="text-white text-xs" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Current</Badge>
                        </div>
                        {(templateSettings.selected_image || templateSettings.main_image) === templateSettings.main_image && (
                          <div className="absolute top-2 right-2">
                            <CheckCircle2 className="w-5 h-5 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </button>
                    )}
                    
                    {/* Additional scraped images */}
                    {templateSettings.additional_images?.slice(0, 5).map((img, idx) => {
                      const imgUrl = typeof img === 'string' ? img : img?.url;
                      if (!imgUrl) return null;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            const newSettings = {...templateSettings, selected_image: imgUrl, main_image: imgUrl};
                            quickUpdate(newSettings);
                          }}
                          className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                            (templateSettings.selected_image || templateSettings.main_image) === imgUrl
                              ? 'border-afa-navy ring-2 ring-afa-navy'
                              : 'border-slate-200 hover:border-afa-navy'
                          }`}
                        >
                          <img 
                            src={imgUrl} 
                            alt={typeof img === 'object' ? img?.alt : `Option ${idx + 2}`} 
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          {(templateSettings.selected_image || templateSettings.main_image) === imgUrl && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 className="w-5 h-5 text-white drop-shadow-lg" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Image Position Control */}
                  {templateSettings.main_image && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">Image Position</Label>
                      <p className="text-xs text-slate-500 mb-3">Choose how the image is cropped in the banner</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'top', label: 'Top', icon: '⬆️' },
                          { value: 'center', label: 'Center', icon: '⬤' },
                          { value: 'bottom', label: 'Bottom', icon: '⬇️' }
                        ].map((pos) => (
                          <button
                            key={pos.value}
                            onClick={() => {
                              const newSettings = {...templateSettings, image_position: pos.value};
                              quickUpdate(newSettings);
                            }}
                            className={`py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium flex items-center justify-center gap-2 ${
                              (templateSettings.image_position || 'center') === pos.value
                                ? 'border-afa-navy bg-afa-navy-light text-afa-navy'
                                : 'border-slate-200 hover:border-afa-navy text-slate-600'
                            }`}
                            data-testid={`image-position-${pos.value}`}
                          >
                            <span>{pos.icon}</span>
                            {pos.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
                )}
              </Card>


              {/* 3. Layout & Design */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.layout ? 'brand-card-expanded' : 'brand-card'}`} onMouseEnter={() => scrollPreviewTo('sections')}>
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('layout')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle style={{fontFamily: 'Manrope'}}>Choose Layout</CardTitle>
                      <CardDescription className="text-xs">Click a layout to preview it instantly</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>{templateSettings.layout || 'magazine'}</Badge>
                      {expandedSections.layout ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.layout && (
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {layouts.map((layout) => (
                      <button
                        key={layout.id}
                        onClick={() => {
                          const newSettings = {...templateSettings, layout: layout.id};
                          quickUpdate(newSettings);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          templateSettings.layout === layout.id 
                            ? 'border-afa-navy bg-afa-navy-light' 
                            : 'border-slate-200 hover:border-afa-navy'
                        }`}
                        data-testid={`layout-${layout.id}`}
                      >
                        {/* Layout preview illustration */}
                        <div className="w-full h-16 rounded-lg mb-3 bg-slate-100 flex items-center justify-center overflow-hidden">
                          {layout.id === 'magazine' && (
                            <div className="w-full h-full p-2 flex flex-col gap-1">
                              <div className="h-4 w-full bg-slate-300 rounded"></div>
                              <div className="flex gap-1 flex-1">
                                <div className="w-1/2 bg-slate-200 rounded"></div>
                                <div className="w-1/2 flex flex-col gap-1">
                                  <div className="h-2 bg-slate-300 rounded"></div>
                                  <div className="h-2 bg-slate-300 rounded w-3/4"></div>
                                </div>
                              </div>
                            </div>
                          )}
                          {layout.id === 'story' && (
                            <div className="w-full h-full p-2 flex flex-col gap-1">
                              <div className="h-6 w-full bg-slate-200 rounded"></div>
                              <div className="flex gap-1 flex-1">
                                <div className="w-6 h-6 bg-slate-400 rounded-full flex-shrink-0"></div>
                                <div className="flex-1 flex flex-col gap-1">
                                  <div className="h-2 bg-slate-300 rounded"></div>
                                  <div className="h-2 bg-slate-300 rounded w-2/3"></div>
                                </div>
                              </div>
                            </div>
                          )}
                          {layout.id === 'cards' && (
                            <div className="w-full h-full p-2 flex flex-col gap-1">
                              <div className="h-5 w-full bg-white rounded shadow-sm border border-slate-200"></div>
                              <div className="h-5 w-full bg-white rounded shadow-sm border border-slate-200"></div>
                              <div className="h-5 w-full bg-afa-navy-light rounded shadow-sm"></div>
                            </div>
                          )}
                        </div>
                        <p className="font-semibold text-sm text-slate-900">{layout.name}</p>
                        <p className="text-xs text-slate-500 line-clamp-2">{layout.description}</p>
                      </button>
                    ))}
                  </div>
                  
                  {/* Magazine Layout Style - only show when Magazine is selected */}
                  {templateSettings.layout === 'magazine' && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <Label className="text-sm font-semibold text-slate-700 mb-3 block">Image Arrangement</Label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const newSettings = {...templateSettings, magazine_style: 'zigzag'};
                            quickUpdate(newSettings);
                          }}
                          className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                            (templateSettings.magazine_style || 'zigzag') === 'zigzag'
                              ? 'border-afa-navy bg-afa-navy-light'
                              : 'border-slate-200 hover:border-afa-navy'
                          }`}
                          data-testid="magazine-style-zigzag"
                        >
                          <div className="flex flex-col items-center gap-1 mb-2">
                            <div className="w-full flex gap-1 h-3">
                              <div className="w-1/3 bg-slate-300 rounded"></div>
                              <div className="w-2/3 bg-slate-200 rounded"></div>
                            </div>
                            <div className="w-full flex gap-1 h-3">
                              <div className="w-2/3 bg-slate-200 rounded"></div>
                              <div className="w-1/3 bg-slate-300 rounded"></div>
                            </div>
                          </div>
                          <p className="font-semibold text-sm text-slate-900">Zigzag</p>
                          <p className="text-xs text-slate-500">Alternating sides</p>
                        </button>
                        <button
                          onClick={() => {
                            const newSettings = {...templateSettings, magazine_style: 'uniform'};
                            quickUpdate(newSettings);
                          }}
                          className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                            templateSettings.magazine_style === 'uniform'
                              ? 'border-afa-navy bg-afa-navy-light'
                              : 'border-slate-200 hover:border-afa-navy'
                          }`}
                          data-testid="magazine-style-uniform"
                        >
                          <div className="flex flex-col items-center gap-1 mb-2">
                            <div className="w-full flex gap-1 h-3">
                              <div className="w-1/3 bg-slate-300 rounded"></div>
                              <div className="w-2/3 bg-slate-200 rounded"></div>
                            </div>
                            <div className="w-full flex gap-1 h-3">
                              <div className="w-1/3 bg-slate-300 rounded"></div>
                              <div className="w-2/3 bg-slate-200 rounded"></div>
                            </div>
                          </div>
                          <p className="font-semibold text-sm text-slate-900">Uniform</p>
                          <p className="text-xs text-slate-500">All images left</p>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Number of Sections */}
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Label className="text-sm font-semibold text-slate-700 mb-3 block">Number of Sections</Label>
                    <div className="flex gap-2">
                      {[2, 3, 4, 5].map((num) => (
                        <button
                          key={num}
                          onClick={() => handleUpdateSections(num)}
                          disabled={updatingSections}
                          className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all text-sm font-medium ${
                            (templateSettings.num_sections || 4) === num
                              ? 'border-afa-navy bg-afa-navy-light text-afa-navy'
                              : 'border-slate-200 hover:border-afa-navy text-slate-600'
                          }`}
                          data-testid={`sections-${num}`}
                        >
                          {updatingSections ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : num}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">How many content blocks in your email</p>
                  </div>
                </CardContent>
                )}
              </Card>


              {/* 4. Choose Theme */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.theme ? 'brand-card-expanded' : 'brand-card'}`}>
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('theme')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle style={{fontFamily: 'Manrope'}}>Choose Theme</CardTitle>
                      <CardDescription className="text-xs">Select a style for your email</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>{themes.find(t => t.id === templateSettings.theme)?.name || 'Modern'}</Badge>
                      {expandedSections.theme ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.theme && (
                <CardContent>
                  {/* General Themes */}
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">General Styles</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                    {themes.filter(t => !t.description?.includes('Industry:')).map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => {
                          const newSettings = {...templateSettings, theme: theme.id};
                          quickUpdate(newSettings, `Theme: ${theme.name}`);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          templateSettings.theme === theme.id 
                            ? 'border-afa-navy bg-afa-navy-light' 
                            : 'border-slate-200 hover:border-afa-navy'
                        }`}
                        data-testid={`theme-${theme.id}`}
                      >
                        <div 
                          className="w-full h-12 rounded-lg mb-2 flex items-center justify-center"
                          style={{ backgroundColor: theme.preview_bg }}
                        >
                          <div 
                            className="w-3/4 h-6 rounded"
                            style={{ backgroundColor: theme.preview_card }}
                          />
                        </div>
                        <p className="font-semibold text-sm text-slate-900">{theme.name}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{theme.description}</p>
                      </button>
                    ))}
                  </div>
                  
                  {/* Industry-Specific Themes */}
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Industry-Specific</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {themes.filter(t => t.description?.includes('Industry:')).map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => {
                          const newSettings = {...templateSettings, theme: theme.id};
                          quickUpdate(newSettings, `Theme: ${theme.name}`);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          templateSettings.theme === theme.id 
                            ? 'border-afa-navy bg-afa-navy-light' 
                            : 'border-slate-200 hover:border-afa-navy'
                        }`}
                        data-testid={`theme-${theme.id}`}
                      >
                        <div 
                          className="w-full h-12 rounded-lg mb-2 flex items-center justify-center"
                          style={{ backgroundColor: theme.preview_bg }}
                        >
                          <div 
                            className="w-3/4 h-6 rounded"
                            style={{ backgroundColor: theme.preview_card }}
                          />
                        </div>
                        <p className="font-semibold text-sm text-slate-900">{theme.name}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{theme.description?.replace('Industry: ', '')}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
                )}
              </Card>


              {/* 5. Edit Your Email */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.editEmail ? 'brand-card-expanded' : 'brand-card'}`} onMouseEnter={() => scrollPreviewTo('headline')}>
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('editEmail')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle style={{fontFamily: 'Manrope'}}>Edit Your Email</CardTitle>
                      <CardDescription className="text-xs">
                        {templateSettings.ai_generated 
                          ? "AI-written content ready to send"
                          : "Edit headlines, text, and CTA"
                        }
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {templateSettings.ai_generated && <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>AI</Badge>}
                      {expandedSections.editEmail ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.editEmail && (
                <CardContent className="space-y-5">
                  {/* Headline Block */}
                  <div className={`transition-all ${!showBlocks.headline ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Headline</Label>
                      <div className="flex items-center gap-2">
                        <button 
                          disabled={loadingHeadlineAI}
                          onClick={async () => {
                            setLoadingHeadlineAI(true);
                            try {
                              const scraped = campaign?.scraped_data || {};
                              // Use body_content first (most content), then description, then title, then current headline
                              const content = scraped.body_content || scraped.description || scraped.title || templateSettings.headline || '';
                              const res = await authAxios.post(`/campaigns/${id}/headline-suggestions`, { scraped_content: content });
                              if (res.data.headlines?.length > 0) {
                                setHeadlineSuggestions(res.data.headlines);
                                setSummarySuggestions(res.data.summaries || []);
                                setShowHeadlineSuggestions(true);
                              }
                            } catch (err) {
                              toast.error("Could not get AI suggestions");
                            } finally {
                              setLoadingHeadlineAI(false);
                            }
                          }}
                          className="text-xs text-afa-navy hover:text-afa-teal flex items-center gap-1 bg-afa-navy-light px-2 py-1 rounded-lg disabled:opacity-50"
                          data-testid="ai-headline-btn"
                        >
                          {loadingHeadlineAI ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {loadingHeadlineAI ? 'Generating...' : 'AI Ideas'}
                        </button>
                        <button 
                          onClick={() => toggleBlock('headline')}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                        >
                          {showBlocks.headline ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {showBlocks.headline ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                    {showHeadlineSuggestions && headlineSuggestions.length > 0 && (
                      <div className="mb-3 p-3 bg-afa-navy-light rounded-xl">
                        <p className="text-xs text-slate-500 mb-2">Click to use:</p>
                        <div className="space-y-1">
                          {headlineSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                debouncedTextUpdate({...templateSettings, headline: suggestion});
                                setShowHeadlineSuggestions(false);
                              }}
                              className="block w-full text-left text-sm text-afa-navy hover:bg-white p-2 rounded-lg transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                        <button 
                          onClick={() => setShowHeadlineSuggestions(false)}
                          className="text-xs text-slate-400 mt-2 hover:text-slate-600"
                        >
                          Close
                        </button>
                      </div>
                    )}
                    {showBlocks.headline && (
                      <Input
                        data-testid="headline-input"
                        value={templateSettings.headline}
                        onChange={(e) => debouncedTextUpdate({...templateSettings, headline: e.target.value})}
                        placeholder="Your headline"
                        className="h-12 rounded-xl"
                      />
                    )}
                  </div>

                  {/* Summary Block */}
                  <div className={`transition-all ${!showBlocks.subheadline ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Summary</Label>
                      <div className="flex items-center gap-2">
                        <button 
                          disabled={loadingHeadlineAI}
                          onClick={async () => {
                            if (summarySuggestions.length > 0) {
                              setShowSummarySuggestions(true);
                            } else {
                              setLoadingHeadlineAI(true);
                              try {
                                const scraped = campaign?.scraped_data || {};
                                // Use body_content first (most content), then description, then title, then current summary
                                const content = scraped.body_content || scraped.description || scraped.title || templateSettings.subheadline || '';
                                const res = await authAxios.post(`/campaigns/${id}/headline-suggestions`, { scraped_content: content });
                                if (res.data.summaries?.length > 0) {
                                  setHeadlineSuggestions(res.data.headlines || []);
                                  setSummarySuggestions(res.data.summaries);
                                  setShowSummarySuggestions(true);
                                }
                              } catch (err) {
                                toast.error("Could not get AI suggestions");
                              } finally {
                                setLoadingHeadlineAI(false);
                              }
                            }
                          }}
                          className="text-xs text-afa-navy hover:text-afa-teal flex items-center gap-1 bg-afa-navy-light px-2 py-1 rounded-lg disabled:opacity-50"
                          data-testid="ai-summary-btn"
                        >
                          {loadingHeadlineAI && !summarySuggestions.length ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {loadingHeadlineAI && !summarySuggestions.length ? 'Generating...' : 'AI Ideas'}
                        </button>
                        <button 
                          onClick={() => toggleBlock('subheadline')}
                          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                        >
                          {showBlocks.subheadline ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {showBlocks.subheadline ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                    {showSummarySuggestions && summarySuggestions.length > 0 && (
                      <div className="mb-3 p-3 bg-afa-navy-light rounded-xl">
                        <p className="text-xs text-slate-500 mb-2">Click to use:</p>
                        <div className="space-y-1">
                          {summarySuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                debouncedTextUpdate({...templateSettings, subheadline: suggestion});
                                setShowSummarySuggestions(false);
                              }}
                              className="block w-full text-left text-sm text-afa-navy hover:bg-white p-2 rounded-lg transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                        <button 
                          onClick={() => setShowSummarySuggestions(false)}
                          className="text-xs text-slate-400 mt-2 hover:text-slate-600"
                        >
                          Close
                        </button>
                      </div>
                    )}
                    {showBlocks.subheadline && (
                      <Input
                        data-testid="subheadline-input"
                        value={templateSettings.subheadline}
                        onChange={(e) => debouncedTextUpdate({...templateSettings, subheadline: e.target.value})}
                        placeholder="A brief description"
                        className="h-12 rounded-xl"
                      />
                    )}
                  </div>

                  {/* Body Block */}
                  <div className={`transition-all ${!showBlocks.body ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Main Content</Label>
                      <button 
                        onClick={() => toggleBlock('body')}
                        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                      >
                        {showBlocks.body ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showBlocks.body ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {showBlocks.body && (
                      <textarea
                        data-testid="body-text-input"
                        value={templateSettings.body_text}
                        onChange={(e) => debouncedTextUpdate({...templateSettings, body_text: e.target.value})}
                        placeholder="The main content of your email..."
                        rows={6}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-afa-navy resize-none"
                      />
                    )}
                  </div>

                  {/* Section Images - New Improved UI */}
                  <div className="pt-4 border-t border-slate-100" onMouseEnter={() => scrollPreviewTo('sections')}>
                    <Label className="text-sm font-semibold text-slate-700 mb-3 block">
                      <span className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-afa-navy" />
                        Add Images and Update Headline Copy
                      </span>
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">Customize section headers and add images. Drag to reorder.</p>
                    
                    <SectionImageManager
                      numSections={templateSettings.num_sections || 4}
                      sectionImages={sectionImages}
                      sectionTexts={templateSettings.sections || {}}
                      customTitles={templateSettings.section_titles || {}}
                      onSectionImagesChange={(newImages) => {
                        setSectionImages(newImages);
                        // Debounce section image updates too
                        if (saveTimeoutRef.current) {
                          clearTimeout(saveTimeoutRef.current);
                        }
                        saveTimeoutRef.current = setTimeout(() => {
                          authAxios.post(`/campaigns/${id}/section-images`, { section_images: newImages })
                            .then((res) => {
                              if (res.data) {
                                setCampaign(prev => prev ? {
                                  ...prev,
                                  email_content: res.data.email_content || prev.email_content
                                } : prev);
                              }
                            })
                            .catch(() => toast.error("Failed to update section images"));
                        }, 500);
                      }}
                      onAddImageClick={(sectionIdx) => setSelectingImageFor(sectionIdx)}
                      onTitlesChange={(newTitles) => {
                        const newSettings = {...templateSettings, section_titles: newTitles};
                        debouncedTextUpdate(newSettings);
                      }}
                      onFetchSuggestions={async (paragraphText, sectionIndex) => {
                        try {
                          const res = await authAxios.post(`/campaigns/${id}/title-suggestions`, {
                            paragraph: paragraphText,
                            section_index: sectionIndex
                          });
                          return res.data.suggestions || [];
                        } catch (err) {
                          console.error("Failed to fetch AI suggestions:", err);
                          return [];
                        }
                      }}
                      onShortenText={async (sectionIndex, sectionText) => {
                        try {
                          const res = await authAxios.post(`/campaigns/${id}/shorten-section`, {
                            section_index: sectionIndex,
                            section_text: sectionText
                          });
                          if (res.data.shortened_text) {
                            // Update local state with shortened text
                            const newSections = {...templateSettings.sections, [sectionIndex]: res.data.shortened_text};
                            setTemplateSettings(prev => ({...prev, sections: newSections}));
                            // Update email content if returned
                            if (res.data.email_content) {
                              setCampaign(prev => prev ? {...prev, email_content: res.data.email_content} : prev);
                            }
                            toast.success("Section shortened!");
                          }
                        } catch (err) {
                          console.error("Failed to shorten section:", err);
                          toast.error("Could not shorten text");
                        }
                      }}
                    />
                    
                    {/* Image Picker Dialog */}
                    <ImagePicker
                      open={selectingImageFor !== null}
                      onClose={() => setSelectingImageFor(null)}
                      onSelect={(imageUrl) => {
                        if (selectingImageFor !== null) {
                          handleSelectSectionImage(selectingImageFor, imageUrl);
                        }
                      }}
                      images={[
                        ...(templateSettings.main_image ? [{ url: templateSettings.main_image, alt: 'Main Image' }] : []),
                        ...(templateSettings.additional_images || [])
                      ]}
                      title={`Select Image for Section ${(selectingImageFor || 0) + 1}`}
                    />
                  </div>

                  {/* AI Tools - Collapsible */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <button 
                      onClick={() => toggleSection('aiTools')}
                      className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-afa-navy" />
                        <span className="text-sm font-medium text-slate-700">AI Tools</span>
                        {templateSettings.ai_generated && (
                          <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Active</Badge>
                        )}
                      </div>
                      {expandedSections.aiTools ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    
                    {expandedSections.aiTools && (
                      <div className="p-4 space-y-4 bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Let AI write your content</span>
                          <Button
                            onClick={handleGenerateContent}
                            disabled={generatingContent}
                            variant="outline"
                            className="border-afa-navy text-afa-navy hover:bg-afa-navy-light"
                            data-testid="generate-content-btn"
                          >
                            {generatingContent ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing...</>
                            ) : (
                              <><Sparkles className="w-4 h-4 mr-2" /> {templateSettings.ai_generated ? 'Rewrite' : 'Write with AI'}</>
                            )}
                          </Button>
                        </div>
                        
                        {templateSettings.ai_generated && (
                          <div className="flex items-center gap-2 p-3 bg-afa-navy-light rounded-xl">
                            <Sparkles className="w-4 h-4 text-afa-navy" />
                            <span className="text-sm text-afa-navy font-medium">AI-written content</span>
                            <Badge className="text-white text-xs ml-auto" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Ready to Send</Badge>
                          </div>
                        )}
                        
                        {/* Length Adjustment Controls */}
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <span className="text-sm font-medium text-slate-700">Email Length</span>
                          <div className="flex gap-2">
                            <div className="relative group">
                              <Button
                                onClick={() => handleAdjustLength('shorter', 'medium')}
                                disabled={adjustingLength}
                                variant="outline"
                                size="sm"
                                className="border-slate-200 text-slate-600 hover:bg-white"
                                data-testid="make-shorter-btn"
                              >
                                {adjustingLength ? <Loader2 className="w-3 h-3 animate-spin" /> : <Minus className="w-3 h-3 mr-1" />}
                                Shorter
                              </Button>
                              <div className="absolute top-full left-0 mt-1 hidden group-hover:block z-10">
                                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1">
                                  <button onClick={() => handleAdjustLength('shorter', 'slight')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Slightly shorter</button>
                                  <button onClick={() => handleAdjustLength('shorter', 'medium')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Medium shorter</button>
                                  <button onClick={() => handleAdjustLength('shorter', 'significant')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Much shorter</button>
                                </div>
                              </div>
                            </div>
                            <div className="relative group">
                              <Button
                                onClick={() => handleAdjustLength('longer', 'medium')}
                                disabled={adjustingLength}
                                variant="outline"
                                size="sm"
                                className="border-slate-200 text-slate-600 hover:bg-white"
                                data-testid="make-longer-btn"
                              >
                                {adjustingLength ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3 mr-1" />}
                                Longer
                              </Button>
                              <div className="absolute top-full right-0 mt-1 hidden group-hover:block z-10">
                                <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1">
                                  <button onClick={() => handleAdjustLength('longer', 'slight')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Slightly longer</button>
                                  <button onClick={() => handleAdjustLength('longer', 'medium')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Medium longer</button>
                                  <button onClick={() => handleAdjustLength('longer', 'significant')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded">Much longer</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
                )}
              </Card>

              {/* 6. Call to Action & Footer */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.ctaFooter ? 'brand-card-expanded' : 'brand-card'}`}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('ctaFooter')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        Call to Action & Footer
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Button link and footer settings
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {templateSettings.cta_text && (
                        <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Set</Badge>
                      )}
                      {expandedSections.ctaFooter ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.ctaFooter && (
                <CardContent className="space-y-5">
                  {/* CTA Block */}
                  <div className={`transition-all ${!showBlocks.cta ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-slate-700">Call to Action Button</Label>
                      <button 
                        onClick={() => toggleBlock('cta')}
                        className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                      >
                        {showBlocks.cta ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showBlocks.cta ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {showBlocks.cta && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Input
                            data-testid="cta-text-input"
                            value={templateSettings.cta_text}
                            onChange={(e) => debouncedTextUpdate({...templateSettings, cta_text: e.target.value})}
                            placeholder="Read More"
                            className="h-12 rounded-xl"
                          />
                          <span className="text-xs text-slate-400 mt-1 block">Button text</span>
                        </div>
                        <div>
                          <Input
                            data-testid="cta-url-input"
                            value={templateSettings.cta_url}
                            onChange={(e) => debouncedTextUpdate({...templateSettings, cta_url: e.target.value})}
                            placeholder="https://..."
                            className="h-12 rounded-xl"
                          />
                          <span className="text-xs text-slate-400 mt-1 block">Button link</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-6 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateSettings.show_articles}
                        onChange={(e) => quickUpdate({...templateSettings, show_articles: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-300 text-afa-navy focus:ring-afa-navy"
                      />
                      <span className="text-sm text-slate-700">Show Articles</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateSettings.show_social}
                        onChange={(e) => quickUpdate({...templateSettings, show_social: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-300 text-afa-navy focus:ring-afa-navy"
                      />
                      <span className="text-sm text-slate-700">Show Social Links</span>
                    </label>
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">Footer Text</Label>
                    <Input
                      data-testid="footer-text-input"
                      value={templateSettings.footer_text}
                      onChange={(e) => debouncedTextUpdate({...templateSettings, footer_text: e.target.value})}
                      placeholder="You're receiving this email because..."
                      className="h-12 rounded-xl"
                    />
                  </div>
                </CardContent>
                )}
              </Card>

              {/* 8. Email Background */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.background ? 'brand-card-expanded' : 'brand-card'}`}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('background')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        <span className="flex items-center gap-2">
                          <Palette className="w-4 h-4 text-afa-navy" />
                          Email Background
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Choose the background color for your email
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-slate-200" 
                        style={{ backgroundColor: templateSettings.bg_color || '#FFFFFF' }}
                      />
                      {expandedSections.background ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.background && (
                <CardContent>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Preset background options */}
                    {[
                      { color: '#FFFFFF', name: 'White' },
                      { color: '#F8FAFC', name: 'Light Gray' },
                      { color: '#F1F5F9', name: 'Cool Gray' },
                      { color: '#FEF3C7', name: 'Warm Cream' },
                      { color: '#FAF5F0', name: 'Linen' },
                      { color: '#0F172A', name: 'Dark' },
                    ].map((bg) => (
                      <button
                        key={bg.color}
                        onClick={() => {
                          const newSettings = {...templateSettings, bg_color: bg.color};
                          quickUpdate(newSettings);
                        }}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                          (templateSettings.bg_color || '#FFFFFF').toUpperCase() === bg.color
                            ? 'ring-2 ring-offset-2 ring-afa-navy'
                            : 'hover:bg-slate-50'
                        }`}
                        title={bg.name}
                      >
                        <div 
                          className={`w-10 h-10 rounded-lg shadow-sm ${bg.color === '#FFFFFF' ? 'border border-slate-200' : ''}`}
                          style={{ backgroundColor: bg.color }}
                        />
                        <span className="text-xs text-slate-600">{bg.name}</span>
                      </button>
                    ))}
                    
                    {/* Custom color picker */}
                    <div className="flex flex-col items-center gap-1 p-2">
                      <input
                        type="color"
                        value={templateSettings.bg_color || '#FFFFFF'}
                        onChange={(e) => {
                          const newSettings = {...templateSettings, bg_color: e.target.value.toUpperCase()};
                          quickUpdate(newSettings);
                        }}
                        className="w-10 h-10 rounded-lg cursor-pointer border-2 border-dashed border-slate-300 hover:border-afa-navy"
                        title="Pick custom background"
                      />
                      <span className="text-xs text-slate-600">Custom</span>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>


              {/* 8. Accent Color */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.accentColor ? 'brand-card-expanded' : 'brand-card'}`} onMouseEnter={() => scrollPreviewTo('headline')}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('accentColor')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        <span className="flex items-center gap-2">
                          <Palette className="w-4 h-4 text-afa-navy" />
                          Accent Color
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Choose your button and link colors
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-slate-200" 
                        style={{ backgroundColor: templateSettings.primary_color || '#635BFF' }}
                      />
                      {expandedSections.accentColor ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.accentColor && (
                <CardContent className="space-y-4">
                  {/* Detected brand colors from website */}
                  {templateSettings.brand_colors?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-2">From your website</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {templateSettings.brand_colors?.map((color, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              const newSettings = {...templateSettings, primary_color: color};
                              quickUpdate(newSettings);
                            }}
                            className={`w-10 h-10 rounded-xl transition-all shadow-sm ${
                              templateSettings.primary_color === color 
                                ? 'ring-2 ring-offset-2 ring-afa-navy scale-110' 
                                : 'hover:scale-105'
                            }`}
                            style={{ backgroundColor: color }}
                            title={`Use ${color}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Preset color options */}
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Preset colors</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {colorPresets.map((preset) => (
                        <button
                          key={preset.color}
                          onClick={() => {
                            const newSettings = {...templateSettings, primary_color: preset.color};
                            quickUpdate(newSettings);
                          }}
                          className={`w-10 h-10 rounded-full transition-all ${
                            templateSettings.primary_color === preset.color 
                              ? 'ring-2 ring-offset-2 ring-afa-navy scale-110' 
                              : 'hover:scale-105'
                          }`}
                          style={{ backgroundColor: preset.color }}
                          title={preset.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Custom color picker */}
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Custom color</p>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={templateSettings.primary_color || '#7c3aed'}
                        onChange={(e) => {
                          const newSettings = {...templateSettings, primary_color: e.target.value.toUpperCase()};
                          quickUpdate(newSettings);
                        }}
                        className="w-12 h-12 rounded-xl cursor-pointer border-2 border-dashed border-slate-300 hover:border-afa-navy"
                        title="Pick custom color"
                      />
                      <div className="text-sm">
                        <p className="font-medium text-slate-700">Pick any color</p>
                        <p className="text-slate-500 text-xs font-mono">{templateSettings.primary_color || '#7c3aed'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>

              {/* 9. Social Media Links (Optional) */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.social ? 'brand-card-expanded' : 'brand-card'}`}>
                <CardHeader className="cursor-pointer" onClick={() => toggleSection('social')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Share2 className="w-5 h-5 text-afa-teal" />
                      <div>
                        <CardTitle style={{fontFamily: 'Manrope'}} className="text-base">Social Media Links</CardTitle>
                        <CardDescription className="text-xs">Optional — add links to your profiles</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(templateSettings.social_facebook || templateSettings.social_instagram || templateSettings.social_twitter || templateSettings.social_linkedin) && (
                        <Badge className="text-white text-xs" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>Added</Badge>
                      )}
                      {expandedSections.social ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.social && (
                  <CardContent className="space-y-3 pt-0">
                    <p className="text-xs text-slate-500 mb-3">Leave blank any you don't want to include</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Facebook</label>
                        <Input
                          placeholder="https://facebook.com/yourpage"
                          value={templateSettings.social_facebook || ''}
                          onChange={(e) => quickUpdate({...templateSettings, social_facebook: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Instagram</label>
                        <Input
                          placeholder="https://instagram.com/yourpage"
                          value={templateSettings.social_instagram || ''}
                          onChange={(e) => quickUpdate({...templateSettings, social_instagram: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Twitter / X</label>
                        <Input
                          placeholder="https://twitter.com/yourhandle"
                          value={templateSettings.social_twitter || ''}
                          onChange={(e) => quickUpdate({...templateSettings, social_twitter: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">LinkedIn</label>
                        <Input
                          placeholder="https://linkedin.com/company/yours"
                          value={templateSettings.social_linkedin || ''}
                          onChange={(e) => quickUpdate({...templateSettings, social_linkedin: e.target.value})}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* 10. Image Library */}
              <Card className={`bg-white border-slate-100 rounded-2xl ${expandedSections.sectionImages ? 'brand-card-expanded' : 'brand-card'}`}>
                <CardHeader className="pb-3 cursor-pointer" onClick={() => toggleSection('sectionImages')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base" style={{fontFamily: 'Manrope'}}>
                        <span className="flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-afa-navy" />
                          Image Library
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Upload and manage images for your email
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {campaign?.additional_images?.length > 0 && (
                        <Badge className="text-xs text-white" style={{backgroundColor: 'rgba(0, 56, 88, 0.7)'}}>{campaign.additional_images.length} images</Badge>
                      )}
                      {expandedSections.sectionImages ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                </CardHeader>
                {expandedSections.sectionImages && (
                <CardContent>
                  {/* Upload from desktop option */}
                  <div className="mb-4 pb-4 border-b border-slate-100">
                    <label 
                      className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                        uploadingHeaderImage ? 'border-afa-navy bg-afa-navy-light' : 'border-slate-300 hover:border-afa-navy hover:bg-afa-navy-light'
                      }`}
                    >
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          try {
                            const res = await authAxios.post(`/campaigns/${id}/upload-image`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            if (res.data.image_url) {
                              // Add to additional_images
                              const newImages = [...(templateSettings.additional_images || []), res.data.image_url];
                              quickUpdate({...templateSettings, additional_images: newImages});
                              toast.success("Image uploaded!");
                            }
                          } catch (err) {
                            toast.error("Failed to upload image");
                          }
                          e.target.value = '';
                        }}
                        data-testid="library-image-upload"
                      />
                      <Upload className="w-6 h-6 text-slate-400 mb-1" />
                      <span className="text-sm font-medium text-slate-600">Upload image to library</span>
                      <span className="text-xs text-slate-400">PNG, JPG, GIF, WebP — max 5MB</span>
                    </label>
                  </div>
                  
                  {/* Pull images from URL */}
                  <div className="mb-4 pb-4 border-b border-slate-100">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Add images from a webpage</Label>
                    <div className="flex gap-2">
                      <Input
                        value={pullImagesUrl}
                        onChange={(e) => setPullImagesUrl(e.target.value)}
                        placeholder="https://example.com/page-with-images"
                        className="flex-1 h-10 rounded-lg text-sm"
                      />
                      <Button
                        onClick={handlePullImages}
                        disabled={pullingImages || !pullImagesUrl}
                        variant="outline"
                        size="sm"
                        className="h-10"
                      >
                        {pullingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pull Images'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Image gallery */}
                  {templateSettings.additional_images && templateSettings.additional_images.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-slate-500">
                          Your image library
                          {templateSettings.additional_images.length > 20 && !showAllImages && (
                            <span className="ml-1">(showing first 20 of {templateSettings.additional_images.length})</span>
                          )}
                        </p>
                        {templateSettings.additional_images.length > 20 && (
                          <button
                            type="button"
                            onClick={() => setShowAllImages(!showAllImages)}
                            className="text-xs font-semibold text-afa-navy hover:underline"
                            data-testid="image-library-toggle-all"
                          >
                            {showAllImages ? "Show first 20" : `Show all (${templateSettings.additional_images.length})`}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {(showAllImages
                          ? templateSettings.additional_images
                          : templateSettings.additional_images.slice(0, 20)
                        ).map((img, idx) => {
                          const imgUrl = typeof img === 'string' ? img : img?.url;
                          if (!imgUrl) return null;
                          return (
                            <div
                              key={idx}
                              className="relative aspect-video rounded-xl overflow-hidden border-2 border-slate-200 group"
                              data-testid={`image-library-item-${idx}`}
                            >
                              <img
                                src={imgUrl}
                                alt={typeof img === 'object' ? img?.alt : `Image ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = templateSettings.additional_images.filter((_, i) => i !== idx);
                                  quickUpdate({...templateSettings, additional_images: newImages});
                                  toast.success("Image removed");
                                }}
                                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                title="Remove from library"
                                data-testid={`image-library-delete-${idx}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-6">No images yet. Upload or pull images to get started.</p>
                  )}
                </CardContent>
                )}
              </Card>

            </div>

            {/* Live Preview - Sticky */}
            <div className="lg:sticky lg:top-6 lg:self-start">
              <Card className="bg-white border-slate-100 rounded-2xl">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div className="flex items-center gap-3">
                    <CardTitle style={{fontFamily: 'Manrope'}}>Live Preview</CardTitle>
                    {isSaving && (
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 animate-pulse" data-testid="saving-indicator">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Saving...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Having Trouble? Button - Always visible in header */}
                    <button 
                      onClick={() => setShowTroubleHelp(true)}
                      className="bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-100 transition-all flex items-center gap-1.5"
                      data-testid="having-trouble-btn-customize"
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      Having Trouble?
                    </button>
                    <Tabs value={previewMode} onValueChange={setPreviewMode}>
                      <TabsList className="h-9">
                        <TabsTrigger value="desktop" className="text-xs px-3">Desktop</TabsTrigger>
                        <TabsTrigger value="mobile" className="text-xs px-3">Mobile</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedPreview(true)}
                      className="h-9 w-9 p-0 rounded-lg border-slate-200 hover:bg-afa-navy-light hover:border-afa-navy"
                      title="Expand preview"
                      data-testid="expand-preview-btn"
                    >
                      <Maximize2 className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="email-preview-frame">
                    {previewMode === "mobile" ? (
                      /* Mobile Preview uses iframe so CSS media queries work */
                      <div className="flex justify-center py-4 bg-slate-100 rounded-xl">
                        <div className="bg-slate-800 rounded-[2rem] p-2 shadow-xl">
                          <div className="bg-slate-800 h-6 flex justify-center items-center relative">
                            <div className="bg-black w-20 h-5 rounded-b-xl absolute top-0"></div>
                          </div>
                          <iframe
                            srcDoc={campaign.email_content}
                            title="Mobile Email Preview"
                            className="w-[375px] h-[667px] rounded-b-[1.5rem] border-0"
                            style={{ display: 'block', background: 'transparent' }}
                          />
                        </div>
                      </div>
                    ) : (
                      /* Desktop Preview - Use iframe with scaling for accurate rendering */
                      <div className="bg-slate-100 rounded-xl overflow-hidden" style={{ height: '550px' }}>
                        <div 
                          className="origin-top"
                          style={{ 
                            transform: 'scale(0.75)', 
                            transformOrigin: 'top center',
                            width: '133%',
                            marginLeft: '-16.5%'
                          }}
                        >
                          <iframe
                            srcDoc={campaign.email_content}
                            title="Desktop Email Preview"
                            className="w-full border-0 bg-white"
                            style={{ display: 'block', height: '733px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Troubleshooting Help Modal for Customize Tab */}
              <TroubleshootingHelp isOpen={showTroubleHelp} onClose={() => setShowTroubleHelp(false)} />
            </div>
          </div>
        )}

        {/* "Next" step CTA — only on Customize tab for drafts */}
        {activeTab === "editor" && campaign.status === "draft" && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#0d9488]/5 to-[#B7EB1B]/5 border border-[#0d9488]/20" data-testid="next-step-cta-editor">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0d9488]/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-[#0d9488]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Looks good?</p>
                <p className="text-sm text-slate-600">Step 2 of 3 — review how it'll look to your recipients.</p>
              </div>
            </div>
            <Button
              onClick={() => setActiveTab("preview")}
              data-testid="next-step-preview-btn"
              className="bg-[#119AB3] hover:bg-[#119AB3]/90 text-white rounded-full font-semibold px-6 h-11"
            >
              Preview my email <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Send Tab - Recipients & Sending Options */}
        {activeTab === "send" && (
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Status Banner */}
            {campaign.status === "scheduled" && campaign.scheduled_at && (
              <Card className="bg-amber-50 border-amber-100 rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-800">Scheduled to Send</p>
                        <p className="text-amber-700">
                          {new Date(campaign.scheduled_at).toLocaleString(undefined, {
                            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })} UTC
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleUnschedule}
                      className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      Cancel Schedule
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {campaign.status === "sent" && (
              <Card className="bg-emerald-50 border-emerald-100 rounded-2xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-800">Campaign Sent!</p>
                      <p className="text-emerald-700">
                        Sent to {campaign.email_list?.length || 0} recipients on {campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recipients Section */}
            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                      <Users className="w-5 h-5 text-afa-teal" />
                      Recipients
                    </CardTitle>
                    <CardDescription>Who should receive this email?</CardDescription>
                  </div>
                  <Badge className="text-lg px-4 py-1">{campaign.email_list?.length || 0} contacts</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign.email_list?.length > 0 ? (
                  <>
                    <ScrollArea className="h-48 border rounded-xl p-3">
                      <div className="space-y-2">
                        {campaign.email_list.map((e, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                            <div className="w-8 h-8 bg-afa-navy-light rounded-full flex items-center justify-center">
                              <span className="text-sm font-semibold text-afa-navy">
                                {e.email.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{e.name || "Contact"}</p>
                              <p className="text-xs text-slate-500">{e.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    <div className="flex gap-2">
                      <label htmlFor="send-tab-upload" className="cursor-pointer flex-1">
                        <input
                          id="send-tab-upload"
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleRecipientUpload}
                          className="hidden"
                        />
                        <Button variant="outline" className="w-full rounded-full" asChild>
                          <span><Upload className="w-4 h-4 mr-2" /> Replace List</span>
                        </Button>
                      </label>
                      <Button
                        variant="outline"
                        onClick={handleUseAllContacts}
                        className="flex-1 rounded-full"
                      >
                        <Users className="w-4 h-4 mr-2" /> Use My Contacts
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-600 font-medium mb-2">No recipients added yet</p>
                    <p className="text-sm text-slate-500 mb-6">Upload your contact list or use saved contacts</p>
                    <div className="flex justify-center gap-3">
                      <label htmlFor="send-tab-upload-empty" className="cursor-pointer">
                        <input
                          id="send-tab-upload-empty"
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleRecipientUpload}
                          className="hidden"
                        />
                        <Button className="bg-afa-navy hover:bg-afa-navy-dark rounded-full" asChild>
                          <span><Upload className="w-4 h-4 mr-2" /> Upload Excel/CSV</span>
                        </Button>
                      </label>
                      <Button
                        variant="outline"
                        onClick={handleUseAllContacts}
                        className="rounded-full"
                      >
                        <Users className="w-4 h-4 mr-2" /> Use My Contacts
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Test Email */}
            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <Mail className="w-5 h-5 text-afa-teal" />
                  Test Your Email
                </CardTitle>
                <CardDescription>Send a preview to yourself before sending to everyone</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={() => setShowTestEmailModal(true)}
                  className="w-full rounded-full h-12"
                >
                  <Mail className="w-4 h-4 mr-2" /> Send Test Email
                </Button>
              </CardContent>
            </Card>

            {/* Send Actions */}
            {campaign.status === "draft" && (
              <Card className="bg-gradient-to-br from-afa-navy to-afa-teal border-0 rounded-2xl text-white">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Manrope'}}>Ready to Send?</h3>
                    <p className="text-white/80">
                      {campaign.email_list?.length > 0 
                        ? `Your email will be sent to ${campaign.email_list.length} recipients`
                        : "Add recipients above to enable sending"}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={() => setShowScheduleModal(true)}
                      variant="outline"
                      disabled={!campaign.email_list?.length}
                      className="rounded-full px-8 py-6 bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <CalendarIcon className="w-5 h-5 mr-2" /> Schedule for Later
                    </Button>
                    <Button
                      onClick={handleSend}
                      disabled={sending || !campaign.email_list?.length}
                      className="rounded-full px-8 py-6 bg-afa-lime text-afa-navy hover:bg-afa-lime/90 font-bold text-lg"
                    >
                      {sending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                      Send Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social Share Option */}
            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                  <Share2 className="w-5 h-5 text-afa-teal" />
                  Share on Social Media
                </CardTitle>
                <CardDescription>Generate social posts from your email content</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowShareModal(true);
                    if (!sharePost) handleGenerateSocialPost("facebook");
                  }}
                  className="w-full rounded-full h-12"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Create Social Posts
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Campaign Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 bg-slate-50 rounded-xl text-center">
                    <Send className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                    <p className="text-3xl font-bold text-slate-900">{campaign.analytics?.sent || 0}</p>
                    <p className="text-sm text-slate-500">Emails Sent</p>
                  </div>
                  <div className="p-6 bg-slate-50 rounded-xl text-center">
                    <MailOpen className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                    <p className="text-3xl font-bold text-slate-900">{campaign.analytics?.opened || 0}</p>
                    <p className="text-sm text-slate-500">Opened</p>
                  </div>
                  <div className="p-6 bg-slate-50 rounded-xl text-center">
                    <MousePointerClick className="w-8 h-8 mx-auto mb-3 text-amber-600" />
                    <p className="text-3xl font-bold text-slate-900">{campaign.analytics?.clicked || 0}</p>
                    <p className="text-sm text-slate-500">Clicked</p>
                  </div>
                  <div className="p-6 bg-slate-50 rounded-xl text-center">
                    <AlertCircle className="w-8 h-8 mx-auto mb-3 text-rose-600" />
                    <p className="text-3xl font-bold text-slate-900">{campaign.analytics?.failed || 0}</p>
                    <p className="text-sm text-slate-500">Failed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-100 rounded-2xl">
              <CardHeader>
                <CardTitle style={{fontFamily: 'Manrope'}}>Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Open Rate</span>
                      <span className="text-sm font-bold text-emerald-600">
                        {campaign.analytics?.sent > 0 
                          ? Math.round((campaign.analytics?.opened / campaign.analytics?.sent) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <Progress 
                      value={campaign.analytics?.sent > 0 
                        ? (campaign.analytics?.opened / campaign.analytics?.sent) * 100 
                        : 0} 
                      className="h-3" 
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Click Rate</span>
                      <span className="text-sm font-bold text-amber-600">
                        {campaign.analytics?.sent > 0 
                          ? Math.round((campaign.analytics?.clicked / campaign.analytics?.sent) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <Progress 
                      value={campaign.analytics?.sent > 0 
                        ? (campaign.analytics?.clicked / campaign.analytics?.sent) * 100 
                        : 0} 
                      className="h-3" 
                    />
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Delivery Rate</span>
                      <span className="text-sm font-bold text-blue-600">
                        {campaign.analytics?.sent > 0 
                          ? Math.round(((campaign.analytics?.sent - campaign.analytics?.failed) / campaign.analytics?.sent) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <Progress 
                      value={campaign.analytics?.sent > 0 
                        ? ((campaign.analytics?.sent - campaign.analytics?.failed) / campaign.analytics?.sent) * 100 
                        : 0} 
                      className="h-3" 
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Expanded Preview Modal */}
      {expandedPreview && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                Full Preview
              </h2>
              <div className="flex items-center gap-3">
                <Tabs value={previewMode} onValueChange={setPreviewMode}>
                  <TabsList className="h-10">
                    <TabsTrigger value="desktop" className="px-4">Desktop</TabsTrigger>
                    <TabsTrigger value="mobile" className="px-4">Mobile</TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExpandedPreview(false)}
                  className="h-10 w-10 p-0 rounded-lg"
                  data-testid="close-expanded-preview"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-100">
              {previewMode === "mobile" ? (
                <div className="flex justify-center">
                  <div className="bg-slate-800 rounded-[2.5rem] p-3 shadow-xl">
                    <div className="bg-slate-800 h-7 flex justify-center items-center relative">
                      <div className="bg-black w-24 h-6 rounded-b-2xl absolute top-0"></div>
                    </div>
                    <iframe
                      srcDoc={campaign.email_content}
                      title="Mobile Email Preview"
                      className="w-[375px] h-[700px] rounded-b-[2rem] border-0 bg-white"
                      style={{ display: 'block' }}
                    />
                  </div>
                </div>
              ) : (
                <div className="max-w-[700px] mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
                  <iframe
                    srcDoc={campaign.email_content}
                    title="Desktop Email Preview"
                    className="w-full border-0"
                    style={{ display: 'block', height: '800px' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// Campaigns List
// CampaignsList extracted to @/pages/CampaignsList.jsx
// ContactsPage extracted to @/pages/ContactsPage.jsx
// AnalyticsPage extracted to @/pages/AnalyticsPage.jsx
// OnboardingWizard extracted to @/pages/OnboardingWizard.jsx
// SettingsPage extracted to @/pages/SettingsPage.jsx

export default CampaignDetail;
